<template>
  <DataTable
    id="equipment-table"
    v-model:editingRows="editingRows"
    data-cy="equipment-table"
    :selection="flagged"
    :loading="!allRows"
    :first="itemsPerPage * itemsPageNum"
    :paginator="true"
    :rows-per-page-options="[5, 10, 20, 100]"
    :rows="itemsPerPage"
    :scrollable="false"
    :row-class="
      (data: any) => (data.id === selectedEquipmentId ? 'p-highlight' : '')
    "
    :total-records="totalCount"
    :value="filteredRows"
    :selectionMode="isAdmin ? undefined : 'single'"
    :state-key="sessionStorageKey"
    :frozen-value="firstRow ? [firstRow] : []"
    state-storage="session"
    filter-display="row"
    class="p-datatable-sm editable-cells-table"
    current-page-report-template="Showing {first} to {last} of {totalRecords}"
    dataKey="id"
    editMode="row"
    paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
    responsiveLayout="scroll"
    striped-rows
    removable-sort
    @update:selection="flagged = isAdmin ? $event : []"
    @page="handlePageChanged"
    @row-edit-save="handleUpsert"
    @row-edit-cancel="handleEquipmentCancelled"
    @row-select="handleRowSelect"
  >
    <Column v-if="isAdmin && isOnline" :frozen="true" selectionMode="multiple">
      <!-- Hide checkbox while editing column -->
      <template #editor></template>
    </Column>

    <Column
      v-if="isAdmin"
      :frozen="true"
      :rowEditor="!enableRestore && online"
    ></Column>

    <template #empty>
      <p class="text-center m-5">0 {{ title }} Found</p>
    </template>

    <template #header>
      <div class="flex w-full align-items-center justify-content-between">
        <div class="flex flex-row">
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="searchInputFields.global"
              v-tooltip.hover.top="searchToolTip"
              type="search"
              class="max-w-30rem"
              :placeholder="searchPlaceholder"
              data-cy="equipment-table-input-search"
              @keypress.enter="executeFilter"
              @input="flagged = []"
            />
          </span>
          <Button
            v-tooltip="'Clear Filters'"
            icon="pi pi-ban"
            class="p-button-info ml-2"
            data-cy="clear-filters-button"
            @click="clearFilters"
          />
        </div>
        <LabelledToggle
          v-if="isAdmin"
          v-model="showDeleted"
          text="Show Deleted"
          :disabled="!!firstRow || editingRows.length > 0"
          @update:model-value="flagged = []"
        />
      </div>
    </template>

    <Column
      v-for="(column, i) in columns"
      :key="column.field"
      :field="column.field"
      :sort-field="column.field"
      :header="isAdmin ? column.header : column.headerShort || column.header"
      :sortable="column.sortable"
      :class="column.class"
      :frozen="column.frozen"
      :showFilterMenu="false"
    >
      <template v-if="column.searchable && isAdmin" #filter>
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText
            v-model="searchInputFields[column.field]"
            type="search"
            class="p-column-filter p-inputtext-sm"
            data-cy="column-search-input"
            @keydown.enter="executeFilter"
          />
        </span>
      </template>
      <template #body="{ data, field }">
        <span v-if="column.isCheckbox && data[field]">YES</span>
        <span v-else-if="column.isCheckbox && !data[field]">NO</span>
        <span v-else-if="column.format">{{ column.format(data[field]) }}</span>
        <span v-else> {{ data[field] }}</span>
      </template>
      <!--
        Only render ConverterInput if column field is number and has less than 21
        decimal places for Intl.NumberFormat maximumFractionDigits. If more digits
        just renter InputText  -->
      <template v-if="column.editable" #editor="{ data, field }">
        <ConverterInput
          v-if="
            (column.isNumber && !column.decimalPlaces) ||
            (column.isNumber &&
              column.decimalPlaces &&
              column.decimalPlaces < 21)
          "
          v-model:model-value="data[field]"
          label=""
          :scenario-validation="!isAdmin"
          :disabled="!isOnline || isSubmitting"
          :base-unit="GenericUnits.Generic"
          :model="GenericUnits.options()"
          :decimal-places="column.decimalPlaces"
          :nullable="column.nullable ?? false"
          mode="write"
        />
        <Checkbox
          v-else-if="column.isCheckbox"
          v-model:model-value="data[field]"
          v-tooltip.top="{
            value:
              '\'KOC Only\' and \'Ecopetrol Only\' cannot both be selected.',
            disabled: !eitherKOCorEcopetrolCheck(data, field)
          }"
          :disabled="
            !isOnline || isSubmitting || eitherKOCorEcopetrolCheck(data, field)
          "
          :true-value="data[field] || 'now()'"
          :false-value="null"
          binary
        />

        <!-- 
          Dropdown: Supports both dynamic enum-based dropdowns and legacy boolean dropdowns
          - If column.dropdownOptions is an array: Use it (dynamic enum options from database)
          - Otherwise: Fall back to boolean true/false options (legacy isDropdown: true behavior)
          This maintains backward compatibility while enabling dynamic enum table support
        -->
        <Dropdown
          v-else-if="column.isDropdown"
          v-model="data[field]"
          :options="
            Array.isArray(column.dropdownOptions)
              ? column.dropdownOptions
              : [
                  { label: 'true', value: true },
                  { label: 'false', value: false }
                ]
          "
          optionLabel="label"
          optionValue="value"
          :disabled="!isOnline || isSubmitting"
        />
        <InputText
          v-else
          v-model="data[field]"
          class="not-converter"
          :autofocus="i === 0"
          :disabled="!isOnline || isSubmitting"
          @focusin="($event.target as HTMLInputElement).select()"
        />
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { ref, inject, computed, isRef } from "vue";
import { useOnline } from "@vueuse/core";
import { injectStrict } from "@/utils/utils";
import { GenericUnits } from "@/libs/units/generic";
import type {
  BaseEquipment,
  UseEquipment
} from "@/composables/equipment/equipmentTypes";
import ConverterInput from "@/components/ConverterInput.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";

const emit = defineEmits<{
  (event: "select", equipment: { data: BaseEquipment }): void;
}>();

const isOnline = useOnline();
const equipment = injectStrict<UseEquipment<BaseEquipment>>("equipment");
const selectedEquipmentId = inject("selected-equipment-id", ref(""));

function handleRowSelect(newSelectedEquipment: { data: BaseEquipment }) {
  if (newSelectedEquipment.data.id !== selectedEquipmentId.value) {
    emit("select", newSelectedEquipment);
  }
}

function eitherKOCorEcopetrolCheck(
  data: BaseEquipment,
  field: string
): boolean {
  const KOC = "only_koc_timestamp";
  const ECO = "only_ecopetrol_timestamp";
  const test = field === KOC || field === ECO;

  // exit early if not KOC or Eciopetrol
  if (!test) {
    return false;
  }
  if (field && field === KOC) {
    return !!data[ECO] && data[ECO] !== null;
  }
  if (field && field === ECO) {
    return !!data[KOC] && data[KOC] !== null;
  }
  // safety
  return false;
}

const online = useOnline();

const {
  searchPlaceholder,
  searchInputFields,
  executeFilter,
  columns: columnsRaw,
  isAdmin,
  showDeleted,
  flagged,
  firstRow,
  editingRows,
  enableRestore,
  itemsPerPage,
  itemsPageNum,
  allRows,
  totalCount,
  filteredRows,
  sessionStorageKey,
  handlePageChanged,
  handleUpsert,
  handleEquipmentCancelled,
  title,
  searchToolTip,
  clearFilters,
  isSubmitting
} = equipment;

// Handle both ComputedRef and plain array for backward compatibility
const columns = computed(() => {
  return isRef(columnsRaw) ? columnsRaw.value : columnsRaw;
});
</script>

<style scoped lang="scss">
:deep(th.p-highlight),
:deep(.p-highlight .p-sortable-column-icon) {
  color: var(--info-500) !important;
}

:deep(.p-datatable-header) {
  padding: 0.5rem 0 1.25rem 0 !important;
  background-color: transparent;
}

.not-converter {
  padding: 0.4rem;
  font-weight: 500;
  font-size: 18px;
  font-family: var(--monospace-family);
  box-shadow: var(--shadow-main);

  &:focus {
    box-shadow: none;
  }
  &::selection {
    background: var(--gray-500);
  }
}
</style>
